import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { AuthGuard, Login } from '@lucid/auth';
import LayoutWrapper from './components/LayoutWrapper/LayoutWrapper';
import Dashboard from './pages/Dashboard/Dashboard';
import Inbox from './pages/Inbox/Inbox';
import Notifications from './pages/Notifications/Notifications';
import TrackOrders from './pages/TrackOrders/TrackOrders';

// Admin
import WorkspaceAccess from './pages/Admin/WorkspaceAccess/WorkspaceAccess';
import PortalSetup from './pages/Admin/PortalSetup/PortalSetup';

// Prospect Desk
import Prospects from './pages/ProspectDesk/Prospects/Prospects';
import CreateNewProspect from './pages/ProspectDesk/CreateNewProspect/CreateNewProspect';

// Order Hub
import OrderHubOrderDesk from './pages/OrderHub/OrderDesk/OrderDesk';
import OrderHubTrackOrders from './pages/OrderHub/TrackOrders/TrackOrders';
import OrderHubOrderHistory from './pages/OrderHub/OrderHistory/OrderHistory';

// Parts Store
import PartsBay from './pages/PartsStore/PartsBay/PartsBay';
import PartsStoreTrackOrders from './pages/PartsStore/TrackOrders/TrackOrders';
import QualityAlerts from './pages/PartsStore/QualityAlerts/QualityAlerts';
import ReturnBuyBack from './pages/PartsStore/ReturnBuyBack/ReturnBuyBack';
import PartsStoreOrderHistory from './pages/PartsStore/OrderHistory/OrderHistory';

// Service Center
import WorkOrderDesk from './pages/ServiceCenter/WorkOrderDesk/WorkOrderDesk';
import Campaigns from './pages/ServiceCenter/Campaigns/Campaigns';
import TechLibrary from './pages/ServiceCenter/TechLibrary/TechLibrary';
import FseTechSupport from './pages/ServiceCenter/FseTechSupport/FseTechSupport';
import CreateTechSupport from './pages/ServiceCenter/CreateTechSupport/CreateTechSupport';

// Claims Center
import CreateClaim from './pages/ClaimsCenter/CreateClaim/CreateClaim';
import TrackClaim from './pages/ClaimsCenter/TrackClaim/TrackClaim';
import Analytics from './pages/ClaimsCenter/Analytics/Analytics';

// Vehicle Hub
import RegisteredVehicle from './pages/VehicleHub/RegisteredVehicle/RegisteredVehicle';
import AssignVehicles from './pages/VehicleHub/AssignVehicles/AssignVehicles';
import FleetHealth from './pages/VehicleHub/FleetHealth/FleetHealth';
import Connectivity from './pages/VehicleHub/Connectivity/Connectivity';
import DocumentCenter from './pages/VehicleHub/DocumentCenter/DocumentCenter';
import CustomerCenter from './pages/VehicleHub/CustomerCenter/CustomerCenter';
import LocateAsset from './pages/VehicleHub/LocateAsset/LocateAsset';

// Customer Care
import CreateCase from './pages/CustomerCare/CreateCase/CreateCase';
import CaseTracker from './pages/CustomerCare/CaseTracker/CaseTracker';
import CustomerCareLocateAsset from './pages/CustomerCare/LocateAsset/CustomerCareLocateAsset';
import SavedCases from './pages/CustomerCare/SavedCases/SavedCases';

// 404
import NotFound from './pages/NotFound/NotFound';

// Define child routes as a reusable array
const appRoutes: RouteObject[] = [
  {
    index: true,
    element: <Dashboard />,
  },
  {
    path: 'inbox',
    element: <Inbox />,
  },
  {
    path: 'notifications',
    element: <Notifications />,
  },
  {
    path: 'track-orders',
    element: <TrackOrders />,
  },
  // Admin Panel
  {
    path: 'admin/workspace-access',
    element: <WorkspaceAccess />,
  },
  {
    path: 'admin/portal-setup',
    element: <PortalSetup />,
  },
  // Prospect Desk
  {
    path: 'prospect-desk/prospects/:prospectId?/:view?',
    element: <Prospects />,
  },
  {
    path: 'prospect-desk/create-new-prospect',
    element: <CreateNewProspect />,
  },
  // Order Hub
  {
    path: 'order-hub/order-desk',
    element: <OrderHubOrderDesk />,
  },
  {
    path: 'order-hub/track-orders',
    element: <OrderHubTrackOrders />,
  },
  {
    path: 'order-hub/order-history',
    element: <OrderHubOrderHistory />,
  },
  // Parts Store
  {
    path: 'parts-store/parts-bay',
    element: <PartsBay />,
  },
  {
    path: 'parts-store/track-orders',
    element: <PartsStoreTrackOrders />,
  },
  {
    path: 'parts-store/quality-alerts',
    element: <QualityAlerts />,
  },
  {
    path: 'parts-store/return-buy-back',
    element: <ReturnBuyBack />,
  },
  {
    path: 'parts-store/order-history',
    element: <PartsStoreOrderHistory />,
  },
  // Service Center
  {
    path: 'service-center/work-order-desk',
    element: <WorkOrderDesk />,
  },
  {
    path: 'service-center/campaigns',
    element: <Campaigns />,
  },
  {
    path: 'service-center/tech-library',
    element: <TechLibrary />,
  },
  {
    path: 'service-center/fse-tech-support',
    element: <FseTechSupport />,
  },
  {
    path: 'service-center/create-tech-support',
    element: <CreateTechSupport />,
  },
  // Claims Center
  {
    path: 'claims-center/track-claim',
    element: <TrackClaim />,
  },
  {
    path: 'claims-center/create-claim',
    element: <CreateClaim />,
  },
  {
    path: 'claims-center/analytics',
    element: <Analytics />,
  },
  // Vehicle Hub
  {
    path: 'vehicle-hub/registered-vehicle',
    element: <RegisteredVehicle />,
  },
  {
    path: 'vehicle-hub/assign-vehicles',
    element: <AssignVehicles />,
  },
  {
    path: 'vehicle-hub/locate-asset',
    element: <LocateAsset />,
  },
  {
    path: 'vehicle-hub/fleet-health',
    element: <FleetHealth />,
  },
  {
    path: 'vehicle-hub/connectivity',
    element: <Connectivity />,
  },
  {
    path: 'vehicle-hub/document-center',
    element: <DocumentCenter />,
  },
  {
    path: 'vehicle-hub/customer-center',
    element: <CustomerCenter />,
  },
  // Customer Care
  {
    path: 'customer-care/create-case',
    element: <CreateCase />,
  },
  {
    path: 'customer-care/vin-related/vehicle-locator',
    element: <CustomerCareLocateAsset />,
  },
  {
    path: 'customer-care/saved-cases',
    element: <SavedCases />,
  },
  {
    path: 'customer-care/case-tracker',
    element: <CaseTracker />,
  },
];

const routes: RouteObject[] = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <LayoutWrapper />
      </AuthGuard>
    ),
    children: appRoutes,
  },
  // Routes with locale prefix (e.g., /en-ca, /de-de, etc.)
  {
    path: '/:locale',
    element: (
      <AuthGuard>
        <LayoutWrapper />
      </AuthGuard>
    ),
    children: appRoutes,
  },
  // 404 - Catch all unmatched routes
  {
    path: '*',
    element: <NotFound />,
  },
];

export const router = createBrowserRouter(routes);

export default routes;
